// NeuroMesh Tokenomics Data Configuration
// This file serves as the single source of truth for all tokenomics data

export const tokenomicsConfig = {
  tokenName: "$NEURO",
  totalSupply: 1000000000, // 1 Billion
  initialFloat: 80, // 80%
  
  // Main allocations
  allocations: [
    {
      id: 'liquidity',
      name: 'Liquidity Provisioning',
      percentage: 22,
      color: 'hsl(192, 75%, 50%)',
      unlockedAtTGE: true,
      description: 'Deep liquidity pools across DEX and CEX platforms for healthy price discovery and minimal slippage.',
      icon: 'Droplets'
    },
    {
      id: 'community',
      name: 'Community & Ecosystem',
      percentage: 19,
      color: 'hsl(168, 70%, 45%)',
      unlockedAtTGE: true,
      description: 'Rewards, incentives, grants, and programs to grow the NeuroMesh ecosystem.',
      icon: 'Users',
      breakdown: [
        { name: 'User Incentives', percentage: 8, color: 'hsl(168, 70%, 55%)' },
        { name: 'Developer Grants', percentage: 5, color: 'hsl(168, 60%, 45%)' },
        { name: 'Validator Programs', percentage: 4, color: 'hsl(168, 50%, 40%)' },
        { name: 'Bug Bounties', percentage: 2, color: 'hsl(168, 40%, 35%)' }
      ]
    },
    {
      id: 'marketing',
      name: 'Marketing & Partnerships',
      percentage: 9,
      color: 'hsl(280, 65%, 55%)',
      unlockedAtTGE: true,
      description: 'Strategic partnerships, exchange listings, and brand awareness campaigns.',
      icon: 'Megaphone'
    },
    {
      id: 'treasury',
      name: 'Treasury & Sustainability',
      percentage: 17,
      color: 'hsl(205, 60%, 45%)',
      unlockedAtTGE: true,
      description: 'Long-term protocol sustainability, emergency reserves, and strategic initiatives.',
      icon: 'Landmark',
      foundationControlled: true
    },
    {
      id: 'rnd',
      name: 'Protocol R&D',
      percentage: 7,
      color: 'hsl(45, 85%, 55%)',
      unlockedAtTGE: true,
      description: 'Research and development for protocol upgrades, security audits, and innovation.',
      icon: 'FlaskConical',
      foundationControlled: true
    },
    {
      id: 'governance',
      name: 'Governance Reserve',
      percentage: 6,
      color: 'hsl(320, 60%, 50%)',
      unlockedAtTGE: true,
      description: 'DAO governance operations, voting incentives, and proposal funding.',
      icon: 'Vote',
      foundationControlled: true
    },
    {
      id: 'investors',
      name: 'Investors',
      percentage: 15,
      color: 'hsl(35, 80%, 50%)',
      unlockedAtTGE: false,
      description: 'Early backers and strategic investors with aligned long-term incentives.',
      icon: 'TrendingUp',
      vesting: {
        cliffMonths: 6,
        vestingMonths: 12,
        type: 'linear'
      }
    },
    {
      id: 'team',
      name: 'Team',
      percentage: 5,
      color: 'hsl(15, 70%, 55%)',
      unlockedAtTGE: false,
      description: 'Core team allocation with extended vesting for long-term alignment.',
      icon: 'Users2',
      vesting: {
        cliffMonths: 24,
        vestingMonths: 36,
        type: 'linear'
      }
    }
  ],
  
  // Foundation-aligned allocations
  foundationTotal: 35, // Treasury + R&D + Governance + Team
  
  // Key metrics for display
  keyMetrics: [
    { label: 'Total Supply', value: 'Fixed', subtext: '1 Billion $NEURO' },
    { label: 'Initial Float', value: '80%', subtext: 'At TGE' },
    { label: 'Liquidity at Launch', value: '22%', subtext: 'Deep pools' },
    { label: 'Locked at TGE', value: '20%', subtext: 'Team & Investors' }
  ],
  
  // Exchange readiness features
  exchangeFeatures: [
    { title: '80% Circulating', description: 'High float at launch prevents manipulation' },
    { title: 'Oversized Liquidity', description: '22% allocated to liquidity pools' },
    { title: 'Community Heavy', description: '19% for ecosystem growth' },
    { title: 'Conservative Team', description: 'Only 5% team allocation' },
    { title: 'Long Lockups', description: '24-month cliff for team tokens' },
    { title: 'Transparent Structure', description: 'On-chain verifiable allocations' },
    { title: 'DAO Ready', description: 'Governance integrated from day one' },
    { title: 'No Hidden Reserves', description: 'All allocations publicly disclosed' }
  ],
  
  // DAO transparency features
  daoFeatures: [
    { title: 'Public Wallets', description: 'Foundation wallets viewable on-chain' },
    { title: 'DAO Governance', description: 'Token holders control key decisions' },
    { title: 'Treasury Dashboard', description: 'Real-time fund tracking' },
    { title: 'No Discretionary Minting', description: 'Fixed supply, no inflation' },
    { title: 'Predictable Emissions', description: 'All unlocks follow published schedule' },
    { title: 'Multisig Controls', description: 'Multiple signers required for treasury' }
  ]
};

// Calculate vesting schedule for a given allocation
export const calculateVesting = (allocation, month) => {
  if (!allocation.vesting) return 100; // No vesting = fully unlocked
  
  const { cliffMonths, vestingMonths } = allocation.vesting;
  
  if (month < cliffMonths) return 0;
  if (month >= cliffMonths + vestingMonths) return 100;
  
  const monthsVested = month - cliffMonths;
  return (monthsVested / vestingMonths) * 100;
};

// Calculate total circulating supply at a given month
export const calculateCirculatingSupply = (month) => {
  return tokenomicsConfig.allocations.reduce((total, allocation) => {
    const vestingPercentage = calculateVesting(allocation, month);
    return total + (allocation.percentage * vestingPercentage / 100);
  }, 0);
};

// Generate vesting timeline data for charts
export const generateVestingTimeline = () => {
  const timeline = [];
  for (let month = 0; month <= 60; month++) {
    const data = {
      month,
      circulating: calculateCirculatingSupply(month),
      investors: calculateVesting(
        tokenomicsConfig.allocations.find(a => a.id === 'investors'),
        month
      ),
      team: calculateVesting(
        tokenomicsConfig.allocations.find(a => a.id === 'team'),
        month
      )
    };
    timeline.push(data);
  }
  return timeline;
};

// Export utility functions
export const formatNumber = (num) => {
  if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
  return num.toString();
};

export const formatPercentage = (num) => {
  return `${num.toFixed(1)}%`;
};
